/**
 * PNG/YAML 번들의 파일 구조와 Dataset 업로드의 Annotation·mask 요약.
 * Owns: 동일 stem 짝, 경로·크기·카메라 검사와 파일 개수.
 * Does not own: 파일 바이트 읽기, YAML 내용 검사, 전송·세트 등록.
 */
export const ANNOTATION_BUNDLE_MAX_BYTES = 128 * 1024 * 1024;
export const ANNOTATION_BUNDLE_MAX_FILES = 500;
export const ANNOTATION_PNG_MAX_BYTES = 32 * 1024 * 1024;
export const ANNOTATION_YAML_MAX_BYTES = 1024 * 1024;

export type AnnotationBundleFile = { relativePath: string; sizeBytes: number };
export type AnnotationBundleIssueCode =
  | "empty"
  | "too_many_files"
  | "total_size_exceeded"
  | "invalid_path"
  | "invalid_size"
  | "unknown_camera"
  | "duplicate_path"
  | "incomplete_pair"
  | "mask_source_missing";
export type AnnotationBundleIssue = {
  code: AnnotationBundleIssueCode;
  count: number;
  samplePath?: string;
};
export type AnnotationBundleAnalysis = {
  fileCount: number;
  totalBytes: number;
  keyframeCount: number | null;
  cameras: readonly { camera: string; keyframeCount: number }[];
  issues: readonly AnnotationBundleIssue[];
};
export type DatasetUploadAssets = {
  annotation: AnnotationBundleAnalysis | null;
  maskCount: number;
  maskBytes: number;
  maskCameras: readonly string[];
  maskIssues: readonly AnnotationBundleIssue[];
};

function addIssue(
  issues: Map<AnnotationBundleIssueCode, AnnotationBundleIssue>,
  code: AnnotationBundleIssueCode,
  path?: string,
): void {
  const issue = issues.get(code);
  if (issue) issue.count += 1;
  else issues.set(code, { code, count: 1, samplePath: path });
}

export function analyzeAnnotationBundle(
  files: readonly AnnotationBundleFile[],
  knownCameras?: ReadonlySet<string>,
): AnnotationBundleAnalysis {
  const totalBytes = files.reduce((sum, file) => sum + file.sizeBytes, 0);
  if (files.length > ANNOTATION_BUNDLE_MAX_FILES) {
    return {
      fileCount: files.length,
      totalBytes,
      keyframeCount: null,
      cameras: [],
      issues: [{ code: "too_many_files", count: files.length }],
    };
  }
  const issues = new Map<AnnotationBundleIssueCode, AnnotationBundleIssue>();
  if (totalBytes > ANNOTATION_BUNDLE_MAX_BYTES) {
    issues.set("total_size_exceeded", { code: "total_size_exceeded", count: totalBytes });
  }
  const seen = new Set<string>();
  const pairs = new Map<string, { camera: string; png: boolean; yaml: boolean }>();
  if (files.length === 0) addIssue(issues, "empty");
  for (const file of files) {
    const path = file.relativePath;
    const parts = path.split("/");
    const filename = parts[1] ?? "";
    const extension = filename.endsWith(".png")
      ? ".png"
      : filename.endsWith(".yaml")
        ? ".yaml"
        : null;
    if (
      path.trim() !== path ||
      path.includes("\\") ||
      Array.from(path).some((char) => {
        const code = char.codePointAt(0)!;
        return code < 32 || code === 127 || (code >= 0xd800 && code <= 0xdfff);
      }) ||
      parts.length !== 2 ||
      parts.some((part) => !part || part.startsWith(".")) ||
      extension === null
    ) {
      addIssue(issues, "invalid_path", path);
      continue;
    }
    if (seen.has(path)) {
      addIssue(issues, "duplicate_path", path);
      continue;
    }
    seen.add(path);
    const camera = parts[0];
    if (knownCameras && !knownCameras.has(camera)) addIssue(issues, "unknown_camera", path);
    const maxBytes = extension === ".png" ? ANNOTATION_PNG_MAX_BYTES : ANNOTATION_YAML_MAX_BYTES;
    if (!Number.isSafeInteger(file.sizeBytes) || file.sizeBytes <= 0 || file.sizeBytes > maxBytes)
      addIssue(issues, "invalid_size", path);
    const stem = path.slice(0, -extension.length);
    const pair = pairs.get(stem) ?? { camera, png: false, yaml: false };
    if (extension === ".png") pair.png = true;
    else pair.yaml = true;
    pairs.set(stem, pair);
  }
  const cameras = new Map<string, number>();
  let keyframeCount = 0;
  for (const [stem, pair] of pairs) {
    if (!pair.png || !pair.yaml) {
      addIssue(issues, "incomplete_pair", stem);
      continue;
    }
    keyframeCount += 1;
    cameras.set(pair.camera, (cameras.get(pair.camera) ?? 0) + 1);
  }
  return {
    fileCount: files.length,
    totalBytes,
    keyframeCount,
    cameras: [...cameras]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([camera, count]) => ({ camera, keyframeCount: count })),
    issues: [...issues.values()],
  };
}

export function analyzeDatasetUploadAssets(
  files: readonly AnnotationBundleFile[],
): DatasetUploadAssets {
  const annotationFiles: AnnotationBundleFile[] = [];
  const cameras = new Set<string>();
  const maskCameras = new Set<string>();
  const missingMasks = new Map<string, { path: string; count: number }>();
  let annotationCount = 0;
  let annotationBytes = 0;
  let maskCount = 0;
  let maskBytes = 0;
  const prefix = "annotation/random_background/";
  for (const file of files) {
    if (file.relativePath.startsWith(prefix)) {
      annotationCount += 1;
      annotationBytes += file.sizeBytes;
      if (annotationFiles.length <= ANNOTATION_BUNDLE_MAX_FILES)
        annotationFiles.push({
          relativePath: file.relativePath.slice(prefix.length),
          sizeBytes: file.sizeBytes,
        });
    }
    const [root, camera, filename, nested] = file.relativePath.split("/");
    if (!camera || !filename || nested !== undefined) continue;
    if ((root === "observation_image" || root === "videos") && filename.endsWith(".mp4"))
      cameras.add(camera);
    if (root === "masks" && /\.(mp4|npy)$/u.test(filename)) {
      maskCount += 1;
      maskBytes += file.sizeBytes;
      maskCameras.add(camera);
      const key = `${camera}/${filename.slice(0, -4)}`;
      const existing = missingMasks.get(key);
      missingMasks.set(key, { path: file.relativePath, count: (existing?.count ?? 0) + 1 });
    }
  }
  // 원본 파일 전체의 별도 집합을 만들지 않고 mask 후보만 제거한다.
  for (const file of files) {
    const [root, camera, filename, nested] = file.relativePath.split("/");
    if (
      (root === "observation_image" || root === "videos") &&
      camera &&
      filename?.endsWith(".mp4") &&
      nested === undefined
    )
      missingMasks.delete(`${camera}/${filename.slice(0, -4)}`);
  }
  const annotation = annotationCount ? analyzeAnnotationBundle(annotationFiles, cameras) : null;
  if (annotation) {
    annotation.fileCount = annotationCount;
    annotation.totalBytes = annotationBytes;
    if (annotationCount > ANNOTATION_BUNDLE_MAX_FILES)
      annotation.issues = [{ code: "too_many_files", count: annotationCount }];
  }
  return {
    annotation,
    maskCount,
    maskBytes,
    maskCameras: [...maskCameras].sort(),
    maskIssues: missingMasks.size
      ? [
          {
            code: "mask_source_missing",
            count: [...missingMasks.values()].reduce((sum, mask) => sum + mask.count, 0),
            samplePath: missingMasks.values().next().value?.path,
          },
        ]
      : [],
  };
}

/** 선택한 Dataset/annotation/번들 루트에서 우선순위가 가장 높은 번들을 고른다. */
export function selectAnnotationImportBundle<FileT extends AnnotationBundleFile>(
  files: readonly FileT[],
  knownCameras: ReadonlySet<string>,
): {
  prefix: string;
  files: readonly FileT[];
  analysis: AnnotationBundleAnalysis;
} {
  const prefix =
    ["annotation/random_background/", "random_background/"].find((candidate) =>
      files.some((file) => file.relativePath.startsWith(candidate)),
    ) ?? "";
  const selected = files
    .filter((file) => file.relativePath.startsWith(prefix))
    .map((file) => ({ ...file, relativePath: file.relativePath.slice(prefix.length) }));
  return { prefix, files: selected, analysis: analyzeAnnotationBundle(selected, knownCameras) };
}
